import { useState } from 'react'
import { DialogTrigger } from 'react-aria-components'
import { Outlet } from 'react-router'
import { Link } from 'react-router-dom'
import { ACCOUNT_PERMISSION } from '../../shared/constants'
import { RoleBase, User } from '../../shared/types'
import { useHasAccountPermission } from '../account-permissions'
import { useAccountRoleListAll } from '../api/account-roles'
import { useUserList, useUserRemove, UsersParamsType } from '../api/users'
import { Button, ButtonLink } from '../components/button'
import { Input } from '../components/input'
import { MultiSelect } from '../components/multi-select'
import { Paper } from '../components/paper'
import { Sidepanel, SidepanelContent, SidepanelFooter } from '../components/sidepanel'
import { List, useList } from '../scaffolding/list'
import { ListHeader } from '../scaffolding/list-header'

export function Users() {
	const [params, setParams] = useList()
	const [filter, setFilter] = useState<UsersParamsType>({})

	const query = useUserList({ ...params, ...filter })
	const { data: roles = {} } = useAccountRoleListAll({
		select: (roles = []) => {
			const keyed: Record<string, RoleBase> = {}
			for (const role of roles) {
				keyed[role.id] = role
			}
			return keyed
		},
	})
	const remove = useUserRemove()
	const canEditUsers = useHasAccountPermission(ACCOUNT_PERMISSION.EDIT_USERS)
	const canDeleteUsers = useHasAccountPermission(ACCOUNT_PERMISSION.DELETE_USERS)

	const onRemove = (id: string) => {
		if (confirm('Opravdu chcete smazat uživatele?')) {
			remove.mutate(id)
		}
	}

	return (
		<Paper>
			<div className="flex justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5">
				<h3 className="text-md font-bold">Seznam uživatelů</h3>
				<DialogTrigger>
					<Button variant="SECONDARY">Filrovat</Button>
					<Sidepanel heading="Filtrovat">
						{({ close }) => (
							<UsersSidepanel onClose={close} filter={filter} onSubmit={setFilter} />
						)}
					</Sidepanel>
				</DialogTrigger>
			</div>
			<List<User>
				params={params}
				setParams={setParams}
				query={query}
				header={
					<tr>
						<ListHeader sortBy="id">ID</ListHeader>
						<ListHeader sortBy="given_name">Jméno</ListHeader>
						<ListHeader sortBy="email">Email</ListHeader>
						<ListHeader sortBy="role">Role</ListHeader>
						<th></th>
					</tr>
				}
			>
				{user => (
					<tr key={user.id}>
						<td width="8%">{user.id}</td>
						<td width="30%">
							{user.given_name ?? ''} {user.family_name ?? ''}
						</td>
						<td width="30%">{user.email}</td>
						<td width="16%">
							{user.account_role_id ? (
								<Link to={`/stp/roles/${user.account_role_id}`}>
									{roles[user.account_role_id]?.name}
								</Link>
							) : (
								<em>Nenastaveno</em>
							)}
						</td>
						<td width="16%" className="flex items-center space-x-2">
							<ButtonLink
								isDisabled={!canEditUsers}
								variant="SECONDARY"
								size="SMALL"
								to={user.id}
							>
								Upravit
							</ButtonLink>
							<Button
								isDisabled={!canDeleteUsers}
								variant="DANGER"
								size="SMALL"
								onPress={() => onRemove(user.id)}
							>
								Smazat
							</Button>
						</td>
					</tr>
				)}
			</List>

			<Outlet />
		</Paper>
	)
}

const rolesToOptions = (roles: RoleBase[]) =>
	roles.map(({ id, name }) => ({ value: id, label: name }))

function UsersSidepanel({
	filter,
	onClose,
	onSubmit,
}: {
	filter: UsersParamsType
	onClose: VoidFunction
	onSubmit: (filter: UsersParamsType) => void
}) {
	const [id, setId] = useState<string>(filter.id ?? '')
	const [email, setEmail] = useState<string>(filter.email ?? '')
	const [accountRoleIds, setAccountRoleIds] = useState<Array<string | number>>(
		filter.accountRoleIds ?? [],
	)

	const { data: roles = [] } = useAccountRoleListAll({
		select: rolesToOptions,
	})

	return (
		<>
			<SidepanelContent className="space-y-4">
				<Input label="ID" value={id} onChange={setId}></Input>
				<Input label="Email" value={email} onChange={setEmail}></Input>
				<MultiSelect
					label="Role"
					options={roles}
					selectedKeys={accountRoleIds}
					onSelectionChange={setAccountRoleIds}
				/>
			</SidepanelContent>
			<SidepanelFooter>
				<Button
					variant="PRIMARY"
					onPress={() => {
						onClose()
						onSubmit({ id, email, accountRoleIds })
					}}
				>
					Povrdit
				</Button>
				<Button
					variant="TEXT"
					size="TEXT"
					onPress={() => {
						onClose()
						onSubmit({})
					}}
				>
					Odstranit vše
				</Button>
			</SidepanelFooter>
		</>
	)
}
